// src/components/LeafletCallTrackingMap.jsx
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  groupCallsByLocation,
  getMapConfig,
  getMarkerStyling,
  generatePopupContent
} from '../utils/leafletUtils';

// Fix for default markers
let DefaultIcon = L.divIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color: #4285f4; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6]
});

L.Marker.prototype.options.icon = DefaultIcon;

const LeafletCallTrackingMap = ({
  callData = [],
  height = '500px',
  showControls = true,
  showLegend = true,
  onLocationClick = null,
  className = '',
  isAdmin = false,
  customerTargets = [],
  priceMap = null,
  isDark = false,
}) => {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef([]);
  const [viewMode, setViewMode] = useState('leads');
  const [mapStats, setMapStats] = useState({
    totalLocations: 0,
    totalCalls: 0,
    totalLeads: 0,
    uniquePhones: 0,
    totalInvestment: 0,
    nicheTotals: {}
  });

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || leafletMapRef.current) return;

    const mapConfig = getMapConfig();

    const map = L.map(mapRef.current, {
      center: mapConfig.center,
      zoom: mapConfig.zoom,
      maxBounds: mapConfig.bounds,
      maxBoundsViscosity: mapConfig.maxBoundsViscosity,
      minZoom: mapConfig.minZoom,
      maxZoom: mapConfig.maxZoom,
      zoomControl: showControls,
      attributionControl: false,
      scrollWheelZoom: false
    });

    // Enable zoom with CMD/Ctrl + scroll
    map.on('wheel', function(e) {
      if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) {
        e.originalEvent.preventDefault();
        map.scrollWheelZoom.enable();
        setTimeout(() => {
          map.scrollWheelZoom.disable();
        }, 100);
      }
    });

    // CartoDB Positron / Dark Matter tile layer
    const tileUrl = isDark
      ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
      : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
    L.tileLayer(tileUrl, {
      attribution: '',
      maxZoom: 18
    }).addTo(map);


    leafletMapRef.current = map;

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, [showControls, isDark]);

  // Update markers when call data OR view mode changes
  useEffect(() => {
    if (!leafletMapRef.current || !Array.isArray(callData)) return;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      leafletMapRef.current.removeLayer(marker);
    });
    markersRef.current = [];

    if (callData.length === 0) {
      setMapStats({
        totalLocations: 0,
        totalCalls: 0,
        totalLeads: 0,
        uniquePhones: 0,
        totalInvestment: 0
      });
      return;
    }

    const locationData = groupCallsByLocation(callData, customerTargets, priceMap);

    const stats = {
      totalLocations: locationData.length,
      totalCalls: locationData.reduce((sum, loc) => sum + loc.totalCalls, 0),
      totalLeads: locationData.reduce((sum, loc) => sum + loc.leadCalls, 0),
      uniquePhones: locationData.reduce((sum, loc) => sum + loc.uniqueCallers, 0),
      totalInvestment: locationData.reduce((sum, loc) => sum + (loc.investment || 0), 0),
      nicheTotals: {}
    };

    locationData.forEach(loc => {
      if (loc.nicheCounts) {
        Object.entries(loc.nicheCounts).forEach(([niche, v]) => {
          if (!stats.nicheTotals[niche]) stats.nicheTotals[niche] = { calls: 0, unique: 0, leads: 0, revenue: 0 };
          stats.nicheTotals[niche].calls += v.calls || 0;
          stats.nicheTotals[niche].unique += v.unique || 0;
          stats.nicheTotals[niche].leads += v.leads || 0;
          stats.nicheTotals[niche].revenue += v.revenue || 0;
        });
      }
    });
    setMapStats(stats);

    // Create markers
    locationData.forEach(location => {
      const markerStyling = getMarkerStyling(location, viewMode, locationData);
      const markerSize = markerStyling.size;
      const markerColor = markerStyling.color;

      let displayValue;
      switch (viewMode) {
        case 'leads':
          displayValue = location.leadCalls;
          break;
        case 'unique':
          displayValue = location.uniqueCallers;
          break;
        case 'revenue':
          displayValue = Math.round(location.investment || 0);
          break;
        case 'calls':
        default:
          displayValue = location.totalCalls;
          break;
      }

      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            background-color: ${markerColor};
            width: ${markerSize}px;
            height: ${markerSize}px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: ${viewMode === 'revenue' ? Math.max(8, markerSize - 12) : Math.max(10, markerSize - 8)}px;
          ">
            ${viewMode === 'revenue'
              ? (displayValue > 9999 ? '9999+' : displayValue)
              : (displayValue > 99 ? '99+' : displayValue)
            }
          </div>
        `,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize/2, markerSize/2],
        popupAnchor: [0, -markerSize/2]
      });

      const marker = L.marker([location.lat, location.lng], {
        icon: customIcon
      });

      const popupContent = generatePopupContent(location, viewMode, isAdmin);
      marker.bindPopup(popupContent, {
        maxWidth: 300,
        closeButton: true,
        autoClose: false,
        closeOnEscapeKey: true
      });

      if (onLocationClick) {
        marker.on('click', () => {
          onLocationClick(location);
        });
      }

      marker.addTo(leafletMapRef.current);
      markersRef.current.push(marker);
    });

    if (locationData.length > 0) {
      const group = new L.featureGroup(markersRef.current);
      const bounds = group.getBounds();

      leafletMapRef.current.fitBounds(bounds, {
        padding: [20, 20],
        maxZoom: 10
      });
    }

  }, [callData, viewMode, onLocationClick, customerTargets, isAdmin, priceMap]);

  // View mode button component
  const ViewModeButton = ({ mode, label, emoji, color }) => (
    <button
      onClick={() => setViewMode(mode)}
      className={`btn-toggle ${viewMode === mode ? 'active' : ''}`}
      data-color={color}
    >
      {emoji} {label}
    </button>
  );

  // Niche emoji helper
  const getNicheEmoji = (niche) => {
    const emojiMap = {
      'Ontstoppen': '🪠',
      'Loodgieter': '🔧',
      'Dakdekker': '🏠',
      'Schoorsteenveger (NL)': '🏗️',
      'UK Projects': '🇬🇧',
      'USA Projects': '🇺🇸',
      'Niet Ingesteld': '❌',
      'Other': '❓'
    };
    return emojiMap[niche] || '🏷️';
  };

  return (
    <div className={`leaflet-map-container ${className}`} style={{ position: 'relative', isolation: 'isolate', zIndex: 0 }}>
      {/* View Mode Controls */}
      <div className="map-controls">
        <ViewModeButton mode="leads" label="Leads" emoji="🎯" color="red" />
        <ViewModeButton mode="unique" label="Uniek" emoji="📱" color="cyan" />
        <ViewModeButton mode="calls" label="Calls" emoji="📞" color="gray" />
        {isAdmin && (
          <ViewModeButton mode="revenue" label="Omzet" emoji="💰" color="green" />
        )}
      </div>

      {/* Map Legend */}
      {showLegend && (
        <div className="map-legend">
          <div className="legend-item">📍 {mapStats.totalLocations} locaties</div>
          <div className="legend-item">📞 {mapStats.totalCalls} totaal</div>
          <div className="legend-item">📱 {mapStats.uniquePhones} uniek</div>
          <div className="legend-item">🎯 {mapStats.totalLeads} leads</div>
          {viewMode === 'revenue' && (
            <div className="legend-item">💰 €{Math.round(mapStats.totalInvestment)} omzet</div>
          )}
          {isAdmin && (() => {
            const nicheEntries = Object.entries(mapStats.nicheTotals || {})
              .filter(([, v]) => {
                switch (viewMode) {
                  case 'leads': return (v.leads || 0) > 0;
                  case 'unique': return (v.unique || 0) > 0;
                  case 'revenue': return (v.revenue || 0) > 0;
                  case 'calls':
                  default: return (v.calls || 0) > 0;
                }
              })
              .map(([niche, v]) => {
                let nicheValue;
                switch (viewMode) {
                  case 'leads': nicheValue = v.leads || 0; break;
                  case 'unique': nicheValue = v.unique || 0; break;
                  case 'revenue': nicheValue = Math.round(v.revenue || 0); break;
                  case 'calls':
                  default: nicheValue = v.calls || 0; break;
                }
                return `${getNicheEmoji(niche)} ${nicheValue}`;
              });

            return nicheEntries.length > 0 ? (
              <div className="legend-item" style={{ marginTop: 'var(--space-2)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-neutral-200)' }}>
                {nicheEntries.join(' • ')}
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* Map Container */}
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: height,
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden'
        }}
      />

      {/* Empty State */}
      {(!callData || callData.length === 0) && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: 'var(--space-6)',
          borderRadius: 'var(--radius-xl)',
          textAlign: 'center',
          zIndex: 1001,
          boxShadow: 'var(--shadow-lg)'
        }}>
          <p style={{ color: 'var(--color-neutral-600)', fontWeight: 'var(--font-medium)' }}>
            {!callData ? 'Loading map...' : 'Geen call data beschikbaar'}
          </p>
        </div>
      )}

      {/* Custom Leaflet CSS */}
      <style>{`
        .leaflet-map-container .leaflet-popup-content-wrapper {
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
          padding: 0;
        }

        .leaflet-map-container .leaflet-popup-content {
          margin: 0;
          width: auto !important;
          min-width: 0;
        }

        .leaflet-map-container .leaflet-popup-tip-container {
          display: none;
        }

        .leaflet-map-container .leaflet-popup-close-button {
          color: #94a3b8;
          font-size: 16px;
          font-weight: 400;
          width: 18px;
          height: 18px;
          padding: 0;
          top: 4px;
          right: 4px;
        }

        .leaflet-map-container .leaflet-popup-close-button:hover {
          color: #475569;
          background: transparent;
        }

        .leaflet-map-container .custom-marker {
          background: none !important;
          border: none !important;
        }

        .leaflet-map-container .leaflet-control-attribution {
          font-size: 10px;
          background: rgba(255, 255, 255, 0.9);
          border-radius: var(--radius-sm);
          padding: 2px 6px;
        }

        .leaflet-map-container .leaflet-control-zoom {
          border-radius: var(--radius-md);
          overflow: hidden;
          border: none;
          box-shadow: var(--shadow-md);
        }

        .leaflet-map-container .leaflet-control-zoom a {
          border-radius: 0;
          border: none;
          color: var(--color-neutral-700);
          font-weight: var(--font-semibold);
        }

        .leaflet-map-container .leaflet-control-zoom a:hover {
          background: var(--color-neutral-100);
          color: var(--color-neutral-900);
        }
      `}</style>
    </div>
  );
};

export default LeafletCallTrackingMap;
